import type { Domain } from "@xinghai/shared";
import { Button, Field, Slider, Stars, Textarea } from "@xinghai/ui";
import { UTTERANCE_ROWS, formatElapsed } from "../labels";
import type { WorkDraft } from "../draft";

export interface UtterancePanelProps {
  domain: Domain;
  draft: WorkDraft;
  onChange: (next: WorkDraft) => void;
  elapsedMs: number;
  /** touched / total characters. */
  progress: { done: number; total: number };
  submitting: boolean;
  onSubmit: () => void;
  /** zod issues from `judgmentSubmitSchema`, already flattened to lines. */
  issues?: string[];
  disabled?: boolean;
  /**
   * docs/ROADMAP.md A3 — 单字任务 (T2) has no meaningful 句级 rubric, so the
   * stars disappear and every utterance dimension submits as `null`.
   * 费力度 / 口音度 / 备注 stay: they are per-recording, not per-sentence.
   */
  hideRubric?: boolean;
  /** docs/UX.md §2.3 — the five ratings sit on one row above the buttons. */
  inlineRubric?: boolean;
}

/** docs/UX.md 7 右栏 — 句级评分 + §5.5 费力度/口音度 + 提交. */
export function UtterancePanel({
  domain,
  draft,
  onChange,
  elapsedMs,
  progress,
  submitting,
  onSubmit,
  issues,
  disabled = false,
  hideRubric = false,
  inlineRubric = false,
}: UtterancePanelProps) {
  const rows = UTTERANCE_ROWS[domain];
  const setStar = (key: string, n: number) =>
    onChange({ ...draft, stars: { ...draft.stars, [key]: n } });

  return (
    <section className="xz-utterance" aria-label="句级评分">
      <header className="xz-utterance__head">
        <h2 className="gz-type-heading">句级评分</h2>
        <span className="gz-caption" aria-label="已用时">
          已用 {formatElapsed(elapsedMs)}
        </span>
      </header>

      {hideRubric ? (
        <p className="gz-caption xz-utterance__norubric">单字任务不评句级。</p>
      ) : (
        <ul
          className={
            inlineRubric ? "xz-utterance__rows xz-utterance__rows--inline" : "xz-utterance__rows"
          }
        >
          {rows.map(([key, label]) => (
            <li key={key} className="xz-utterance__row">
              <span className="xz-utterance__label">{label}</span>
              <Stars
                label={label}
                value={draft.stars[key] ?? null}
                onChange={disabled ? undefined : (n) => setStar(key, n)}
                readOnly={disabled}
              />
              <span className="gz-caption xz-utterance__stars">
                {draft.stars[key] == null ? "未评" : `${draft.stars[key]} / 5`}
              </span>
            </li>
          ))}
        </ul>
      )}

      <Field label="费力度" hint="你听懂这条录音要花多少力气；0 最轻松，1 最费力。">
        <Slider
          value={draft.listening_effort}
          label="费力度"
          step={0.05}
          disabled={disabled}
          onChange={(v) => onChange({ ...draft, listening_effort: v })}
        />
      </Field>

      <Field label="口音度" hint="口音度不计入评分，只单列记录。">
        <Slider
          value={draft.accentedness}
          label="口音度"
          step={0.05}
          disabled={disabled}
          onChange={(v) => onChange({ ...draft, accentedness: v })}
        />
      </Field>

      <Field label="备注">
        <Textarea
          rows={3}
          value={draft.note}
          placeholder="对这条录音的整体说明（可选）"
          disabled={disabled}
          onChange={(e) => onChange({ ...draft, note: e.target.value })}
        />
      </Field>

      <p className="gz-caption xz-utterance__progress" role="status">
        已标注 {progress.done}/{progress.total} 字
      </p>

      {issues && issues.length > 0 && (
        <div className="gz-notice gz-notice--error" role="alert">
          <p>提交的内容还有问题：</p>
          <ul className="xz-utterance__issues">
            {issues.map((i) => (
              <li key={i}>{i}</li>
            ))}
          </ul>
        </div>
      )}

      <Button
        variant="primary"
        size="lg"
        block
        loading={submitting}
        disabled={disabled}
        onClick={onSubmit}
      >
        提交
      </Button>
      <p className="gz-caption">提交后不可撤回。</p>
    </section>
  );
}
